import { Move, MOVE_DAMAGE_CLASS_PHYSICAL, MOVE_DAMAGE_CLASS_SPECIAL, isKabehakaiMove, isKataYaburiMove, isOtoMove, isPhotonGeyserMove, isPsyshockMove, isTerabrastMove } from "../../move/move";
import { OFFENCE_ITEM_ID_INOTINOTAMA, OFFENCE_ITEM_ID_TATSUJIN_OBI, DEFENCE_ITEM_ID_HANGENKINOMI, DEFENCE_ITEM_ID_BALLOON } from "../../types/item";
import { PokemonV2 } from "../../pokemon/pokemon";
import { POKEMON_TYPE_FIRE, POKEMON_TYPE_GROUND } from "../../types/type";
import { calcRankedValue } from "./core-utils";
import type { damageRateMapper } from "../../types/damage";
import { per_130, per_125, per_120, per_075, per_050 } from "./constants";
import { hasBattleStatus, otherSetting } from "../../OtherSetting";
import { isBouon, isHardrock, isIromegane, isKoorinorinpun, isMultiScaleOrShadowShield, isPunkRock, isSurinuke, isTennen, isTeraShell, isWonderGuard } from "../ability";

// 【7】ダメージの補正値
export function calc_damage_rate({
  move,
  offencePokemon,
  deffencePokemon,
  rateMapper,
  loopHitNumber,
  otherSetting,
  isCritical = false,
}: {
  move: Move;
  offencePokemon: PokemonV2;
  deffencePokemon: PokemonV2;
  rateMapper: damageRateMapper;
  loopHitNumber: number;
  otherSetting: otherSetting;
  isCritical?: boolean;
}) {
  let localRate = 4096;
  const isKatayaburiMove = isKataYaburiMove(move);
  const isKabehakaiMoveByName = isKabehakaiMove(move);
  const isPhotonGeyser = isPhotonGeyserMove(move);
  const isPsyshock = isPsyshockMove(move);

  // 防音特性による音技無効化 (かたやぶりで無効化)
  if (isBouon(deffencePokemon) && isOtoMove(move) && !isKatayaburiMove) {
    return 0;
  }

  // ふしぎなまもり: 効果抜群の技以外は無効（かたやぶり技は特性を無視する）
  if (isWonderGuard(deffencePokemon) && rateMapper.compatibilityRate <= 1 && !isKatayaburiMove) {
    return 0;
  }

  // 風船による地面技無効化 (防御側が風船を持っている場合、地面技のダメージを0にする)
  // ただし、かたやぶり技の場合は風船を無視する
  if (
    deffencePokemon.selected_item_id === DEFENCE_ITEM_ID_BALLOON &&
    move.type === POKEMON_TYPE_GROUND &&
    !isKatayaburiMove
  ) {
    return 0;
  }

  // いのちのたま
  if (
    offencePokemon.selected_item_id ===
    OFFENCE_ITEM_ID_INOTINOTAMA
  ) {
    localRate = Math.round(localRate * per_130);
  }

  // たつじんのおび (効果抜群の技に1.2倍)
  if (
    offencePokemon.selected_item_id ===
    OFFENCE_ITEM_ID_TATSUJIN_OBI &&
    rateMapper.compatibilityRate >= 2
  ) {
    localRate = Math.round(localRate * per_120);
  }

  // 防御側の特性によるダメージ補正
  if (
    deffencePokemon.selected_ability?.name_ja === 'かんそうはだ' &&
    move.type === POKEMON_TYPE_FIRE
  ) {
    localRate = Math.round(localRate * per_125);
  }

  // フィルター/ハードロック/プリズムアーマー (効果抜群の技を0.75倍)
  if (isHardrock(deffencePokemon) && rateMapper.compatibilityRate >= 2) {
    localRate = Math.round(localRate * per_075);
  }

  // 半減きのみ (効果抜群の技を0.5倍、連続技の1回目のみ)
  if (
    deffencePokemon.selected_item_id === DEFENCE_ITEM_ID_HANGENKINOMI &&
    rateMapper.compatibilityRate >= 2 &&
    loopHitNumber === 1
  ) {
    localRate = Math.round(localRate * per_050);
  }

  // テラバーストの物理/特殊判定は攻撃・特攻の高い方に合わせる
  let tempMoveDamageClassNumber = move.damage_class_number
  const okAdjustTerabrast = isTerabrastMove(move) && isTerabrastMove(offencePokemon.selected_move) && offencePokemon.terasu_type &&
    calcRankedValue(offencePokemon.effective_value.attack, offencePokemon.rankCorrection.attack) >
    calcRankedValue(offencePokemon.effective_value.special_attack, offencePokemon.rankCorrection.special_attack)
  if (okAdjustTerabrast) {
    tempMoveDamageClassNumber = MOVE_DAMAGE_CLASS_PHYSICAL
  }

  // フォトンゲイザーの動的判定（てんねん特性考慮、かたやぶり技はてんねん無視）
  const photonGeyserAttackRank = (isTennen(deffencePokemon) && !isKatayaburiMove)
    ? 0
    : (offencePokemon.rankCorrection?.attack ?? 0);
  const photonGeyserSpecialAttackRank = (isTennen(deffencePokemon) && !isKatayaburiMove)
    ? 0
    : (offencePokemon.rankCorrection?.special_attack ?? 0);
  const okAdjustPhotonGeyser = isPhotonGeyser &&
    calcRankedValue(offencePokemon.effective_value.attack, photonGeyserAttackRank) >
    calcRankedValue(offencePokemon.effective_value.special_attack, photonGeyserSpecialAttackRank)
  if (okAdjustPhotonGeyser) {
    tempMoveDamageClassNumber = MOVE_DAMAGE_CLASS_PHYSICAL
  }

  // こおりのりんぷん (特殊技を0.5倍、サイコショック系技も適用)
  if (
    isKoorinorinpun(deffencePokemon) &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_SPECIAL || isPsyshock) &&
    !okAdjustPhotonGeyser
  ) {
    localRate = Math.round(localRate * per_050);
  }

  // パンクロック (音技を0.5倍)
  if (
    isPunkRock(deffencePokemon) &&
    isOtoMove(move)
  ) {
    localRate = Math.round(localRate * per_050);
  }

  // リフレクター・ひかりのかべを無視する条件をチェック
  const ignoreBarriers = isCritical ||
    isSurinuke(offencePokemon) ||
    isKabehakaiMoveByName;

  // リフレクター (物理技を0.5倍、急所時・すりぬけ・特定技で無効)
  if (
    !ignoreBarriers &&
    hasBattleStatus(otherSetting, 'reflector') &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_PHYSICAL || okAdjustPhotonGeyser)
  ) {
    localRate = Math.round(localRate * per_050);
  }

  // ひかりのかべ (特殊技を0.5倍、急所時・すりぬけ・特定技で無効、サイコショック系技も適用)
  if (
    !ignoreBarriers &&
    hasBattleStatus(otherSetting, 'light-screen') &&
    (tempMoveDamageClassNumber === MOVE_DAMAGE_CLASS_SPECIAL || isPsyshock) &&
    !okAdjustPhotonGeyser
  ) {
    localRate = Math.round(localRate * per_050);
  }

  // マルチスケイル/ファントムガード (連続技の1回目のみ0.5倍)
  if (
    isMultiScaleOrShadowShield(deffencePokemon) &&
    loopHitNumber === 1
  ) {
    localRate = Math.round(localRate * per_050);
  }

  // テラスシェル (タイプ相性1.0倍以上の技を実質0.5倍にする)
  if (
    isTeraShell(deffencePokemon) &&
    rateMapper.compatibilityRate >= 1
  ) {
    // 相性倍率で割って最終的に0.5倍になるよう補正
    const teraShellRate = per_050 / rateMapper.compatibilityRate;
    localRate = Math.round(localRate * teraShellRate);
  }

  // イナズマドライブ・アクセルブレイク (効果抜群時に5461/4096倍)
  if (
    (move.id === 878 || move.id === 879) && // イナズマドライブ(878) or アクセルブレイク(879)
    rateMapper.compatibilityRate >= 2
  ) {
    localRate = Math.round(localRate * 5461 / 4096);
  }

  // いろめがね (効果今ひとつ時に2倍)
  if (
    isIromegane(offencePokemon) &&
    rateMapper.compatibilityRate < 1
  ) {
    localRate = Math.round(localRate * 2);
  }

  return localRate;
}
